import {
  TicketNotificationMode,
  ticketNotificationModes,
} from '@certhon/user-settings';
import { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  useUpdateUserSettings,
  useUserSettings,
} from '@/features/user-settings/user-settings.hooks';

interface Option {
  value: string;
  label: string;
  description: string;
}

const OPTIONS: Record<TicketNotificationMode, Option> = {
  direct: {
    value: 'direct',
    label: 'Immediately',
    description:
      'An email shortly after someone changes the ticket. Changes made within a few minutes are bundled.',
  },
  digest: {
    value: 'digest',
    label: 'Daily summary',
    description: "One email each morning with yesterday's changes.",
  },
  both: {
    value: 'both',
    label: 'Both',
    description: 'Immediately and in the daily summary.',
  },
  none: {
    value: 'none',
    label: 'Off',
    description: 'No emails about these tickets.',
  },
};

const PERSONAL_OPTION: Option = {
  value: 'direct',
  label: 'Immediately',
  description:
    'An email shortly after someone changes a ticket assigned to you, or mentions you. Cannot be turned off.',
};

interface OptionGroupProps {
  name: string;
  title: string;
  description: ReactNode;
  options: Option[];
  value: string;
  disabled?: boolean;
  /** Fixed choice: full contrast, no interaction. */
  readOnly?: boolean;
  onChange?: (value: string) => void;
}

const OptionGroup = ({
  name,
  title,
  description,
  options,
  value,
  disabled,
  readOnly,
  onChange,
}: OptionGroupProps) => (
  <div className="space-y-3">
    <div>
      <Label>{title}</Label>
      <p className="text-muted-foreground text-xs">{description}</p>
    </div>
    <RadioGroup
      value={value}
      disabled={disabled}
      onValueChange={onChange}
      className={cn('grid gap-2', readOnly && 'pointer-events-none')}
      aria-readonly={readOnly}
    >
      {options.map((option) => (
        <Label
          key={option.value}
          htmlFor={`${name}-${option.value}`}
          className="border-input has-[[data-state=checked]]:border-ring
            has-[[data-state=checked]]:bg-accent has-[:enabled]:hover:bg-accent flex
            items-start gap-3 rounded-md border p-3 transition-colors
            has-[:disabled]:cursor-default has-[:disabled]:opacity-70
            has-[:enabled]:cursor-pointer"
        >
          <RadioGroupItem
            id={`${name}-${option.value}`}
            value={option.value}
            className="mt-0.5"
          />
          <span className="grid gap-0.5">
            <span className="text-sm font-medium">{option.label}</span>
            <span className="text-muted-foreground text-xs font-normal">
              {option.description}
            </span>
          </span>
        </Label>
      ))}
    </RadioGroup>
  </div>
);

/**
 * `settings.ticket_notifications`: how other people's changes on tickets the
 * user is involved in are mailed. The assignee, executor and newly mentioned
 * users are always mailed right away, whatever this says.
 */
export const TicketNotificationsSetting = () => {
  const { ticket_notifications } = useUserSettings();
  const { canEdit, updateSettings } = useUpdateUserSettings();

  return (
    <div className="space-y-6">
      <OptionGroup
        name="ticket-notifications-personal"
        title="Tickets assigned to you, and mentions"
        description="When someone changes a ticket assigned to you, or mentions you."
        options={[PERSONAL_OPTION]}
        value={PERSONAL_OPTION.value}
        readOnly
      />
      <OptionGroup
        name="ticket-notifications"
        title="All tickets you are involved in"
        description="Tickets you created, changed, commented on, are assigned to or were mentioned in, when someone else changes them."
        options={ticketNotificationModes.map((mode) => OPTIONS[mode])}
        value={ticket_notifications}
        disabled={!canEdit}
        onChange={(value) =>
          updateSettings({
            ticket_notifications: value as TicketNotificationMode,
          })
        }
      />
    </div>
  );
};
